import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import { Input, SreenCoverLoading } from "src/components";
import ActionModal from "src/components/YearBooking/components/common/ActionModal";
import AlertModal from "src/components/YearBooking/components/common/AlertModal";
import { usePopupLocation } from "src/components/PopupLocation";
import { useAlert, useGroupLocation, useGroupTypes } from "src/hooks";
import { createTeeshotAxiosInstance } from "src/http";
import { recoilStates } from "src/recoil/recoilStates";
import styled from "styled-components";
import GroupTypeSelectPopup from "src/components/GroupTypeSelectPopup";

export default function YearGroupManage() {
  const [group, setGroup] = useRecoilState(recoilStates.group);
  const groupTypes = useGroupTypes();
  const groupLocations = useGroupLocation();
  const alert = useAlert();
  const [selectedGroupType, setSelectedGroupType] = useState(
    group?.booking_group_character_id || ""
  );
  const [isGroupTypePopupOpen, setIsGroupTypePopupOpen] = useState(false);
  const { PopupLocation, propsToInject, activeChildId, activeParentId } = usePopupLocation({
    defaultSelectIds: group.district_id ? [group.district_id] : [],
    items: groupLocations,
  });
  const { value: nameValue, onChange: handleChangeName } = Input.hooks.useInput(group.name);
  const { value: urlValue, onChange: handleChangeUrl } = Input.hooks.useInput(group.group_sns_url);
  const { value: descValue, onChange: handleChangeDesc } = Input.hooks.useInput(group.group_desc);
  const { base64, fileName, handleFileInput, incodeLoading, previewTempUrl } =
    Input.hooks.useFileInput();
  const [isNewImg, setIsNewImg] = useState(false);
  const navigate = useNavigate();
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const { value: memberCountValue, onChange: handleMemberCountChange } = Input.hooks.useInput(
    String(group.member_count || 0)
  );

  const handleGroupTypeSelect = (groupTypeId: string) => {
    setSelectedGroupType(groupTypeId);
  };

  // ID를 이름으로 변환하는 함수
  const getGroupTypeName = (id: string) => {
    const groupTypeMap = {
      "1": "연습장 동호회",
      "2": "학교 동문회",
      "3": "지역 동호회",
      "4": "온라인 동호회(밴드,카페 등)",
      "5": "기업 동호회",
      "6": "골프 업종 모임",
      "7": "친목 모임",
      "8": "기타 모임",
    };
    return groupTypeMap[id as keyof typeof groupTypeMap] || "선택해주세요";
  };

  return (
    <>
      {isSubmitLoading && <SreenCoverLoading />}
      <Container>
        {/* <PageTitle>단체 정보를 수정해주세요.</PageTitle> */}

        <FormField>
          <FieldLabel>
            <RequiredAsterisk>*</RequiredAsterisk>
            <LabelText>단체명</LabelText>
          </FieldLabel>
          <InputField $active={Boolean(nameValue)}>
            <StyledInput
              value={nameValue}
              onChange={handleChangeName}
              placeholder="단체이름을 입력하세요."
              disabled={!isEditMode}
            />
          </InputField>
        </FormField>

        <FormField>
          <FieldLabel>
            <RequiredAsterisk>*</RequiredAsterisk>
            <LabelText>회원 수</LabelText>
          </FieldLabel>
          <InputField $active={Boolean(memberCountValue)}>
            <StyledInput
              type="number"
              value={memberCountValue}
              onChange={handleMemberCountChange}
              placeholder="예시) 50"
              disabled={!isEditMode}
            />
          </InputField>
        </FormField>

        <FormField>
          <FieldLabel>
            <RequiredAsterisk>*</RequiredAsterisk>
            <LabelText>단체 구분</LabelText>
          </FieldLabel>
          <InputField
            $active={Boolean(selectedGroupType)}
            onClick={() => isEditMode && setIsGroupTypePopupOpen(true)}
            $disabled={!isEditMode}
          >
            <InputText $isPlaceholder={!selectedGroupType}>
              {selectedGroupType ? getGroupTypeName(String(selectedGroupType)) : "선택해주세요"}
            </InputText>
          </InputField>
        </FormField>

        <FormField>
          <FieldLabel>
            <RequiredAsterisk>*</RequiredAsterisk>
            <LabelText>활동 지역</LabelText>
          </FieldLabel>
          <PopupLocation
            items={groupLocations}
            isEssential={true}
            placeholder={"선택해주세요"}
            disabled={!isEditMode}
            {...propsToInject}
          />
        </FormField>

        <FormField>
          <FieldLabel>
            <LabelText>단체 카페/밴드 주소</LabelText>
          </FieldLabel>
          <InputField $active={Boolean(urlValue)}>
            <StyledInput
              value={urlValue}
              onChange={handleChangeUrl}
              placeholder="예시) http://cafe.naver.com/lovegolf, 네이버카페 골프사랑"
              disabled={!isEditMode}
            />
          </InputField>
        </FormField>

        <FormField>
          <FieldLabel>
            <LabelText>단체 소개</LabelText>
          </FieldLabel>
          <TextAreaField $active={Boolean(descValue)}>
            <StyledTextArea
              onChange={handleChangeDesc as any}
              value={descValue}
              placeholder="예시) 사내 골프 동호회입니다."
              disabled={!isEditMode}
            />
          </TextAreaField>
        </FormField>

        <FormField>
          <FieldLabel>
            <LabelText>단체 로고</LabelText>
          </FieldLabel>
          <Input
            fileName={fileName}
            imgUrl={!isNewImg ? group.group_logo_url : previewTempUrl ?? ""}
            attr={{
              type: "file",
              disabled: !isEditMode,
              onChange: (e) => {
                if (isEditMode) {
                  console.log(" imageChagned!:");
                  setIsNewImg(true);
                  handleFileInput(e);
                }
              },
            }}
          />
        </FormField>

        <ButtonContainer>
          <SaveButton
            onClick={() => {
              if (!isEditMode) {
                setIsEditMode(true);
              } else {
                setShowConfirmModal(true);
              }
            }}
          >
            {isEditMode ? "수정 완료" : "수정 하기"}
          </SaveButton>
        </ButtonContainer>

        <GroupTypeSelectPopup
          isOpen={isGroupTypePopupOpen}
          onClose={() => setIsGroupTypePopupOpen(false)}
          onSelect={handleGroupTypeSelect}
          selectedValue={String(selectedGroupType)}
        />

        {/* 확인 모달 */}
        {showConfirmModal && (
          <ActionModal
            title="단체 정보 수정"
            subtitle="단체정보를 수정하시겠습니까?"
            cancelText="뒤로"
            editText="확인"
            onClose={() => setShowConfirmModal(false)}
            onCancel={() => setShowConfirmModal(false)}
            onEdit={async () => {
              const submitData: any = {
                nameValue: nameValue.trim(),
                urlValue: urlValue.trim(),
                descValue: descValue.trim(),
                memberCountValue: memberCountValue || 0,
                groupCharacterId: selectedGroupType,
                groupDistrictId: (() => {
                  if (!activeParentId) {
                    return null;
                  }
                  const adjustedChildId = (() => {
                    if (!activeChildId) {
                      return "000";
                    }
                    if (activeChildId < 100) {
                      return `0${activeChildId}`;
                    }
                    if (activeChildId < 10) {
                      return `00${activeChildId}`;
                    }
                    return `${activeChildId}`;
                  })();
                  return `${activeParentId}${adjustedChildId}`;
                })(),
              };
              if (isNewImg) {
                submitData.groupImgBase64 = base64;
              }
              if (
                !submitData?.nameValue ||
                !submitData?.groupCharacterId ||
                !submitData?.memberCountValue ||
                !submitData?.groupDistrictId
              ) {
                alert("필수 정보 미입력");
                setShowConfirmModal(false);
                return;
              }

              try {
                setIsSubmitLoading(true);
                const res = await createTeeshotAxiosInstance().put("/yearBooking/group", {
                  groupId: group.id,
                  submitData,
                });
                if (!res.data.success) {
                  throw new Error();
                }

                const newGroupData = res.data?.data?.group;
                setGroup((prev) => {
                  return { ...prev, ...newGroupData };
                });

                setShowSuccessModal(true);
              } catch (e: any) {
                alert({
                  content: `그룹정보 변경 실패. ${e?.message || ""}`,
                  onClose: () => alert.resume(),
                });
                console.log("e :", e);
                await alert.pause();
                window.location.reload();
              } finally {
                setIsSubmitLoading(false);
                setShowConfirmModal(false);
              }
            }}
          />
        )}

        {/* 성공 모달 */}
        {showSuccessModal && (
          <AlertModal
            title="단체정보 수정완료"
            subtitle="단체정보가 수정완료되었습니다."
            confirmText="확인"
            onClose={() => setShowSuccessModal(false)}
            onConfirm={() => {
              setShowSuccessModal(false);
              setIsEditMode(false);
              navigate(-1);
            }}
          />
        )}
      </Container>
    </>
  );
}

const Container = styled.div`
  width: 100%;
  padding: 0 20px;
  margin-top: 24px;
`;

const PageTitle = styled.h1`
  color: #1a1a1a;
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
  height: 24px;
  margin: 0 0 40px 0;
`;

const FormField = styled.div`
  margin-bottom: 30px;
`;

const FieldLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  height: 18px;
`;

const RequiredAsterisk = styled.span`
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-weight: 700;
  font-size: 14px;
  color: #f97000;
`;

const LabelText = styled.span`
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-weight: 700;
  font-size: 14px;
  color: #1a1a1a;
`;

const InputField = styled.div<{ $active?: boolean; $disabled?: boolean }>`
  display: flex;
  width: 100%;
  height: 44px;
  padding: 13px 10px;
  align-items: center;
  border-radius: 6px;
  border: 1px solid ${(p) => (p.$active ? "#007bff" : "#d1d5d8")};
  background: ${(p) => (p.$disabled ? "#f0f0f4" : "#fdfdfd")};
  cursor: ${(p) => (p.$disabled ? "not-allowed" : "default")};
  &:focus-within {
    border-color: ${(p) => (p.$disabled ? "#d1d5d8" : "#007bff")};
  }
`;

const StyledInput = styled.input`
  width: 100%;
  border: none;
  outline: none;
  background: transparent;
  color: #1a1a1a;
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;

  &:disabled {
    color: #79818a;
    cursor: not-allowed;
  }

  &::placeholder {
    color: #79818a;
  }
`;

const InputText = styled.span<{ $isPlaceholder?: boolean }>`
  color: ${(p) => (p.$isPlaceholder ? "#79818a" : "#1a1a1a")};
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
`;

const TextAreaField = styled.div<{ $active?: boolean }>`
  display: flex;
  width: 100%;
  min-height: 80px;
  padding: 13px 10px;
  align-items: flex-start;
  border-radius: 6px;
  border: 1px solid ${(p) => (p.$active ? "#007bff" : "#d1d5d8")};
  background: #fdfdfd;
  &:focus-within {
    border-color: #007bff;
  }
`;

const StyledTextArea = styled.textarea`
  width: 100%;
  border: none;
  outline: none;
  background: transparent;
  color: #1a1a1a;
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  resize: vertical;
  min-height: 54px;

  &:disabled {
    color: #79818a;
    cursor: not-allowed;
  }

  &::placeholder {
    color: #79818a;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 40px;
`;

const SaveButton = styled.button`
  display: flex;
  width: 100%;
  max-width: 100%;
  min-width: 170px;
  height: 60px;
  padding: 19px 20px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 10px;
  background: var(--Teeshot-Primary-Color-Teeshot-Green, #004b49);
  color: white;
  border: none;
  font-family: "Pretendard", -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #003a38;
  }

  &:active {
    background-color: #002a28;
  }
`;
